import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * MemoryMatch — Dopamine-inducing card match game for React
 * - No external deps
 * - Levels from 2x2 upward
 * - Combos, stars, coins, achievements, streaks, juicy animations + SFX
 * - Saves progress to localStorage (progress, coins, unlocked themes)
 *
 * Usage:
 *   <MemoryMatch />
 *
 * Optional props:
 *   initialTheme: "neon" | "ocean" | "jungle" | "galaxy" | "candy"
 *   onLevelComplete?: (summary) => void
 */

const EMOJIS = [
  "🐶","🐱","🦊","🐼","🐵","🦄","🐸","🐯","🐷","🐙","🦋","🐞",
  "🍓","🍉","🍍","🍇","🍋","🥝","🍒","🍪","🍰","🍫","🧁","🍭",
  "⚽️","🏀","🏈","🎾","🎲","🧩","🎮","🎧","🎹","🎷","🥁","🎯",
];

const THEMES = {
  neon:    { bg:"#0b1020", card:"#171d3a", accent:"#7cf6e7", glow:"#7cf6e7", text:"#dffcff" },
  ocean:   { bg:"#031f2e", card:"#083c5a", accent:"#39c0f7", glow:"#39c0f7", text:"#d8f5ff" },
  jungle:  { bg:"#081b0f", card:"#143d26", accent:"#5df28a", glow:"#5df28a", text:"#e7ffe7" },
  galaxy:  { bg:"#16072a", card:"#2b1048", accent:"#b86cff", glow:"#b86cff", text:"#f6ebff" },
  candy:   { bg:"#2d0a16", card:"#5b122a", accent:"#ff76a6", glow:"#ff76a6", text:"#ffe5ef" },
};

const GRID_STEPS = [
  [2,2],
  [3,2],
  [4,3],
  [4,4],
  [5,4],
  [6,4],
  [6,5],
  [6,6],
];

const LS_KEY = "mm_save_v1";

function useLocalSave() {
  const [save, setSave] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      return raw ? JSON.parse(raw) : {
        level: 0,
        coins: 0,
        bestTimes: {},
        streak: 0,
        achievements: {},
        unlockedThemes: ["neon","ocean","jungle"],
        lastTheme: "neon",
      };
    } catch {
      return {
        level: 0, coins: 0, bestTimes:{}, streak:0, achievements:{},
        unlockedThemes:["neon","ocean","jungle"], lastTheme:"neon"
      };
    }
  });

  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify(save));
  }, [save]);

  return [save, setSave];
}

// Simple SFX using WebAudio (no files)
function useBeepKit() {
  const ctxRef = useRef(null);
  const ensureCtx = () => (ctxRef.current ??= new (window.AudioContext || window.webkitAudioContext)());
  const beep = (freq=440, time=0.07, type="sine", gain=0.04) => {
    const ctx = ensureCtx();
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    g.gain.value = gain;
    osc.connect(g); g.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + time);
  };
  return {
    flip:   () => beep(600, 0.05, "triangle", 0.03),
    match:  () => { beep(660, 0.06, "sine", 0.05); setTimeout(()=>beep(880,0.08,"sine",0.05),60); },
    miss:   () => beep(180, 0.08, "sawtooth", 0.03),
    levelUp:() => { [660,880,990,1320].forEach((f,i)=>setTimeout(()=>beep(f,0.08,"sine",0.05), i*80)); },
    coin:   () => beep(1200, 0.05, "square", 0.04),
    combo:  () => beep(1000, 0.05, "triangle", 0.04),
    ping:   () => beep(1400, 0.04, "sine", 0.03),
  };
}

function shuffle(arr) {
  const a = [...arr];
  for (let i=a.length-1; i>0; i--) {
    const j = Math.floor(Math.random()*(i+1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function makeDeck(pairsCount) {
  const pool = shuffle(EMOJIS).slice(0, pairsCount);
  const deck = shuffle(pool.flatMap((e, idx) => [
    { id:`${idx}-A`, emoji:e, pairId:idx },
    { id:`${idx}-B`, emoji:e, pairId:idx },
  ]));
  return deck;
}

function formatTime(ms) {
  const s = Math.floor(ms/1000);
  const m = Math.floor(s/60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2,"0")}`;
}

export default function MemoryMatch({ initialTheme, onLevelComplete }) {
  const [save, setSave] = useLocalSave();
  const [themeKey, setThemeKey] = useState(initialTheme || save.lastTheme || "neon");
  const theme = THEMES[themeKey] || THEMES.neon;

  const sfx = useBeepKit();

  // Level/grid
  const levelIndex = save.level ?? 0;
  const [cols, rows] = GRID_STEPS[Math.min(levelIndex, GRID_STEPS.length-1)];
  const totalCards = cols * rows;
  const totalPairs = Math.floor(totalCards/2);

  const [deck, setDeck] = useState(() => makeDeck(totalPairs));
  const [revealed, setRevealed] = useState([]); // ids currently face-up but not matched
  const [matchedIds, setMatchedIds] = useState(new Set());
  const [moves, setMoves] = useState(0);
  const [mismatches, setMismatches] = useState(0);
  const [startTime, setStartTime] = useState(Date.now());
  const [now, setNow] = useState(Date.now());
  const [combo, setCombo] = useState(0);
  const [lastMatchAt, setLastMatchAt] = useState(0);
  const [coinsEarned, setCoinsEarned] = useState(0);
  const [flashMsg, setFlashMsg] = useState(null);
  const [isWin, setIsWin] = useState(false);

  // Timer tick
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(t);
  }, []);

  // Reset when theme or level changes
  useEffect(() => {
    setDeck(makeDeck(totalPairs));
    setRevealed([]);
    setMatchedIds(new Set());
    setMoves(0);
    setMismatches(0);
    setStartTime(Date.now());
    setNow(Date.now());
    setCombo(0);
    setLastMatchAt(0);
    setCoinsEarned(0);
    setFlashMsg(null);
    setIsWin(false);
    // small start ping
    setTimeout(sfx.ping, 200);
    // eslint-disable-next-line
  }, [cols, rows, themeKey]);

  const elapsed = now - startTime;

  // Stars based on speed + accuracy
  const starTarget = Math.max(20, totalPairs * 7); // rough expected time (s)
  const timeScore = (elapsed/1000) <= starTarget ? 2 : (elapsed/1000) <= starTarget*1.4 ? 1 : 0;
  const missPenalty = mismatches === 0 ? 2 : mismatches <= Math.ceil(totalPairs/3) ? 1 : 0;
  const stars = Math.max(1, Math.min(3, timeScore + missPenalty)); // 1..3

  const allMatched = matchedIds.size === totalPairs;

  // Win handling
  useEffect(() => {
    if (!allMatched || isWin) return;
    setIsWin(true);
    sfx.levelUp();

    const baseCoins = 10 + totalPairs + stars*3 + Math.min(combo*2, 10);
    setCoinsEarned(c => c + baseCoins);

    const summary = {
      levelIndex,
      cols, rows, totalPairs,
      timeMs: elapsed, moves, mismatches, stars,
      comboMax: combo, coins: baseCoins,
    };

    // Save progress
    setSave(prev => {
      const next = { ...prev };
      next.level = Math.min(prev.level + 1, GRID_STEPS.length - 1);
      next.coins = (prev.coins ?? 0) + baseCoins;
      next.streak = (prev.streak ?? 0) + 1;
      const key = `${cols}x${rows}`;
      const best = prev.bestTimes?.[key];
      next.bestTimes = { ...(prev.bestTimes||{}) , [key]: !best ? elapsed : Math.min(best, elapsed) };
      next.achievements = awardAchievements(prev.achievements||{}, summary);
      return next;
    });

    onLevelComplete && onLevelComplete(summary);

    // tiny celebration flashes
    flash("LEVEL UP! +" + baseCoins + " 🪙", 2000);
    // eslint-disable-next-line
  }, [allMatched]);

  function awardAchievements(ach, s) {
    const out = { ...ach };
    if (!ach.firstWin) out.firstWin = { name:"First Win", ts: Date.now() };
    if (s.mismatches === 0 && !ach.flawless) out.flawless = { name:"Flawless", ts: Date.now() };
    if ((s.timeMs/1000) <= Math.max(20, s.totalPairs*5) && !ach.speedDemon) out.speedDemon = { name:"Speed Demon", ts: Date.now() };
    if (s.comboMax >= 3 && !ach.comboKing) out.comboKing = { name:"Combo King", ts: Date.now() };
    if ((save.streak ?? 0) + 1 >= 3 && !ach.onAStreak) out.onAStreak = { name:"On a Streak (3+)", ts: Date.now() };
    return out;
  }

  function flash(text, ms=1000) {
    setFlashMsg(text);
    setTimeout(() => setFlashMsg(null), ms);
  }

  function handleFlip(card) {
    if (isWin) return;
    if (revealed.includes(card.id)) return;
    if (matchedIds.has(card.pairId)) return;
    if (revealed.length === 2) return;

    sfx.flip();
    const next = [...revealed, card.id];
    setRevealed(next);

    if (next.length === 2) {
      setMoves(m => m+1);
      const [aId, bId] = next;
      const a = deck.find(c => c.id === aId);
      const b = deck.find(c => c.id === bId);
      if (a.pairId === b.pairId) {
        // match!
        setTimeout(() => {
          setMatchedIds(prev => new Set([...prev, a.pairId]));
          setRevealed([]);
          sfx.match();
          const now = Date.now();
          if (now - lastMatchAt <= 2500) {
            setCombo(c => {
              const nc = Math.min(c+1, 10);
              if (nc >= 2) {
                sfx.combo();
                flash(`COMBO x${nc}!`, 700);
              }
              return nc;
            });
          } else {
            setCombo(1);
            flash(`Nice!`, 400);
          }
          setLastMatchAt(now);
        }, 260);
      } else {
        // miss
        setTimeout(() => {
          sfx.miss();
          setMismatches(mm => mm+1);
          setRevealed([]);
          setCombo(0);
        }, 420);
      }
    }
  }

  function resetProgress() {
    setSave({
      level: 0, coins: 0, bestTimes:{}, streak:0, achievements:{},
      unlockedThemes:["neon","ocean","jungle"], lastTheme: "neon",
    });
    flash("Progress reset", 1200);
  }

  function unlockTheme(key) {
    if ((save.unlockedThemes||[]).includes(key)) return;
    const cost = 80;
    if ((save.coins ?? 0) < cost) {
      flash(`Need ${cost} 🪙 to unlock`, 1200);
      return;
    }
    setSave(prev => ({
      ...prev,
      coins: prev.coins - cost,
      unlockedThemes: [...prev.unlockedThemes, key],
    }));
    flash(`Unlocked ${key} theme!`, 1200);
    sfx.coin();
  }

  useEffect(() => {
    if (themeKey !== save.lastTheme) {
      setSave(prev => ({ ...prev, lastTheme: themeKey }));
    }
    // eslint-disable-next-line
  }, [themeKey]);

  // Build grid style vars
  const styleVars = {
    "--bg": theme.bg,
    "--card": theme.card,
    "--accent": theme.accent,
    "--text": theme.text,
    "--glow": theme.glow,
  };

  return (
    <div style={{...rootStyle, ...styleVars}}>
      <StyleTag />

      <header className="mm-topbar">
        <div className="mm-title">
          <span className="mm-logo">🧠✨</span> Memory Match
        </div>
        <div className="mm-topstats">
          <Badge label="Level" value={`${levelIndex+1}`} />
          <Badge label="Grid" value={`${cols}×${rows}`} />
          <Badge label="Time" value={formatTime(elapsed)} />
          <Badge label="Moves" value={moves} />
          <Badge label="Coins" value={(save.coins ?? 0) + coinsEarned} icon="🪙" />
          <Stars count={stars} />
        </div>
      </header>

      <div className="mm-toolbar">
        <ThemePicker
          themeKey={themeKey}
          setThemeKey={setThemeKey}
          unlocked={save.unlockedThemes || []}
          onUnlock={unlockTheme}
        />
        <div className="mm-actions">
          <button className="mm-btn" onClick={() => {
            // reshuffle current level
            setDeck(makeDeck(totalPairs));
            setRevealed([]); setMatchedIds(new Set());
            setMoves(0); setMismatches(0);
            setStartTime(Date.now()); setNow(Date.now());
            setCombo(0); setLastMatchAt(0); setCoinsEarned(0); setIsWin(false);
            flash("Shuffled!");
            sfx.ping();
          }}>↻ Shuffle</button>
          <button className="mm-btn danger" onClick={resetProgress}>Reset</button>
        </div>
      </div>

      {flashMsg && <div className="mm-flash">{flashMsg}</div>}

      <div
        className="mm-grid"
        style={{ gridTemplateColumns: `repeat(${cols}, 1fr)`}}
      >
        {deck.map(card => {
          const isMatched = matchedIds.has(card.pairId);
          const isFaceUp =
            isMatched || revealed.includes(card.id);
          return (
            <Card
              key={card.id}
              emoji={card.emoji}
              faceUp={isFaceUp}
              matched={isMatched}
              onClick={() => handleFlip(card)}
            />
          );
        })}
      </div>

      <footer className="mm-footer">
        <ComboMeter combo={combo} />
        <AchievementsPanel achievements={save.achievements || {}} />
        <Streak streak={save.streak || 0} />
      </footer>

      {isWin && (
        <WinModal
          stars={stars}
          time={formatTime(elapsed)}
          moves={moves}
          mismatches={mismatches}
          coins={coinsEarned}
          onNext={() => {
            setDeck(makeDeck(totalPairs)); // will be rebuilt by effect on level change
            setIsWin(false);
            // trigger level change by touching themeKey (or rely on save.level change already done)
            setThemeKey(k => k); // no-op to keep modal closing lively
          }}
        />
      )}
    </div>
  );
}

/* ------------ UI bits ------------ */

function Badge({ label, value, icon }) {
  return (
    <div className="mm-badge">
      <div className="mm-badge-label">{icon ? `${icon} ${label}` : label}</div>
      <div className="mm-badge-value">{value}</div>
    </div>
  );
}

function Stars({ count }) {
  return (
    <div className="mm-stars" title={`${count} star rating`}>
      {Array.from({length:3}).map((_,i)=>(
        <span key={i} className={`mm-star ${i<count ? "on" : ""}`}>★</span>
      ))}
    </div>
  );
}

function Card({ emoji, faceUp, matched, onClick }) {
  return (
    <button
      className={`mm-card ${faceUp ? "faceup": ""} ${matched ? "matched": ""}`}
      onClick={onClick}
      disabled={matched}
      aria-label={faceUp ? emoji : "Hidden card"}
    >
      <div className="mm-card-inner">
        <div className="mm-card-front">?</div>
        <div className="mm-card-back">{emoji}</div>
      </div>
    </button>
  );
}

function ComboMeter({ combo }) {
  if (combo <= 1) return <div className="mm-combo">Combo: —</div>;
  const pct = Math.min(100, (combo/10)*100);
  return (
    <div className="mm-combo">
      Combo x{combo}
      <div className="mm-bar">
        <div className="mm-bar-fill" style={{width:`${pct}%`}} />
      </div>
    </div>
  );
}

function Streak({ streak }) {
  return <div className="mm-streak" title="Win streak">🔥 Streak: {streak}</div>;
}

function AchievementsPanel({ achievements }) {
  const list = Object.values(achievements);
  return (
    <div className="mm-ach">
      <div className="mm-ach-title">🏆 Achievements</div>
      {list.length === 0 ? (
        <div className="mm-ach-empty">None yet — go get ’em!</div>
      ) : (
        <div className="mm-ach-grid">
          {list.slice(-6).map((a, i) => (
            <div className="mm-ach-badge" key={i}>{a.name}</div>
          ))}
        </div>
      )}
    </div>
  );
}

function WinModal({ stars, time, moves, mismatches, coins, onNext }) {
  return (
    <div className="mm-modal">
      <div className="mm-modal-card">
        <div className="mm-modal-title">Level Complete!</div>
        <div className="mm-modal-stars">
          {Array.from({length:stars}).map((_,i)=><span key={i}>⭐️</span>)}
        </div>
        <div className="mm-modal-stats">
          <div>⏱ Time: <b>{time}</b></div>
          <div>🃏 Moves: <b>{moves}</b></div>
          <div>❌ Mismatches: <b>{mismatches}</b></div>
          <div>🪙 Coins: <b>+{coins}</b></div>
        </div>
        <button className="mm-btn big" onClick={onNext}>Next Level →</button>
      </div>
    </div>
  );
}

function ThemePicker({ themeKey, setThemeKey, unlocked, onUnlock }) {
  const all = Object.keys(THEMES);
  return (
    <div className="mm-themes">
      {all.map(k => {
        const isUnlocked = unlocked.includes(k);
        return (
          <button
            key={k}
            className={`mm-themechip ${themeKey===k?"active":""} ${isUnlocked?"":"locked"}`}
            onClick={() => isUnlocked ? setThemeKey(k) : onUnlock(k)}
            title={isUnlocked ? `Theme: ${k}` : `Unlock ${k} theme (80 🪙)`}
          >
            <span className="dot" style={{background:THEMES[k].accent}} />
            {k}{!isUnlocked && " 🔒"}
          </button>
        );
      })}
    </div>
  );
}

/* ------------ Inline styles & CSS ------------ */

const rootStyle = {
  "--bg":"#0b1020", "--card":"#171d3a", "--accent":"#7cf6e7", "--text":"#dffcff", "--glow":"#7cf6e7",
  background:"var(--bg)", color:"var(--text)", minHeight:"100%", padding:"16px",
  fontFamily:"Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
};

function StyleTag() {
  return (
    <style>{`
      .mm-topbar {
        display:flex; align-items:center; justify-content:space-between;
        margin-bottom:12px; gap:12px;
      }
      .mm-title { font-weight:800; letter-spacing:0.5px; display:flex; align-items:center; gap:8px; font-size:20px; }
      .mm-logo { filter: drop-shadow(0 0 6px var(--glow)); }
      .mm-topstats { display:flex; gap:8px; flex-wrap:wrap; align-items:center; }

      .mm-badge { background:linear-gradient(180deg, rgba(255,255,255,0.04), rgba(0,0,0,0.2));
        border:1px solid rgba(255,255,255,0.08); padding:8px 10px; border-radius:10px; min-width:70px;
        box-shadow:0 0 12px rgba(0,0,0,0.25) inset; }
      .mm-badge-label { font-size:11px; opacity:0.75; }
      .mm-badge-value { font-weight:800; font-size:14px; }

      .mm-stars { display:flex; gap:2px; margin-left:4px; }
      .mm-star { font-size:16px; opacity:0.25; }
      .mm-star.on { opacity:1; text-shadow:0 0 10px var(--glow); color:var(--accent); }

      .mm-toolbar { display:flex; justify-content:space-between; align-items:center; gap:12px; margin:8px 0 12px; flex-wrap:wrap; }
      .mm-actions { display:flex; gap:8px; }
      .mm-btn { background: var(--card); border:1px solid rgba(255,255,255,0.15); color:var(--text);
        padding:8px 12px; border-radius:10px; cursor:pointer; font-weight:700; transition: transform .08s ease, box-shadow .2s;
        box-shadow:0 0 0 0 var(--glow); }
      .mm-btn:hover { transform: translateY(-1px); box-shadow:0 0 12px rgba(124,246,231,0.25); }
      .mm-btn.big { padding:12px 18px; font-size:16px; }
      .mm-btn.danger { border-color:#ff6b6b66; color:#ffb0b0; }
      .mm-btn:active { transform: translateY(1px) scale(0.98); }

      .mm-themes { display:flex; gap:8px; flex-wrap:wrap; }
      .mm-themechip { display:flex; align-items:center; gap:8px; padding:6px 10px; border-radius:999px;
        background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.1); color:var(--text); cursor:pointer; font-weight:700; }
      .mm-themechip.active { outline:2px solid var(--accent); box-shadow: 0 0 10px rgba(0,0,0,0.3), 0 0 12px var(--glow) inset; }
      .mm-themechip.locked { opacity:0.7; }
      .mm-themechip .dot { width:10px; height:10px; border-radius:50%; display:inline-block; }

      .mm-flash {
        position:fixed; top:16px; left:50%; transform:translateX(-50%);
        background:rgba(0,0,0,0.45); border:1px solid rgba(255,255,255,0.15);
        padding:8px 12px; border-radius:10px; font-weight:800; letter-spacing:0.3px;
        box-shadow: 0 0 18px rgba(0,0,0,0.35), 0 0 12px var(--glow) inset; z-index:50;
      }

      .mm-grid {
        display:grid; gap:10px; max-width:920px; margin:0 auto; width:100%;
      }

      .mm-card {
        position:relative; aspect-ratio: 1/1; border:none; border-radius:14px; cursor:pointer;
        background:transparent; perspective:900px;
        filter: drop-shadow(0 2px 0 rgba(0,0,0,0.15));
      }
      .mm-card:focus { outline:2px solid var(--accent); }
      .mm-card .mm-card-inner {
        position:absolute; inset:0; transition: transform .32s ease;
        transform-style:preserve-3d;
      }
      .mm-card .mm-card-front, .mm-card .mm-card-back {
        position:absolute; inset:0; border-radius:14px; display:flex; align-items:center; justify-content:center;
        font-size: clamp(20px, 6.8vw, 48px); font-weight:900;
        backface-visibility:hidden;
        border:1px solid rgba(255,255,255,0.12);
      }
      .mm-card-front {
        background:linear-gradient(180deg, rgba(255,255,255,0.08), rgba(0,0,0,0.2)), var(--card);
        color: #ffffff90;
        text-shadow: 0 0 8px rgba(0,0,0,0.4);
      }
      .mm-card-back {
        transform: rotateY(180deg);
        background: radial-gradient(1200px 300px at 50% -200px, var(--accent)10%, transparent 60%), var(--card);
        border:1px solid rgba(255,255,255,0.18);
        text-shadow: 0 0 10px var(--glow);
      }
      .mm-card.faceup .mm-card-inner { transform: rotateY(180deg) scale(1.02); }
      .mm-card.matched .mm-card-inner { animation: mm-pop 420ms ease; }
      @keyframes mm-pop {
        0% { transform: rotateY(180deg) scale(1.02); }
        40% { transform: rotateY(180deg) scale(1.10); }
        100% { transform: rotateY(180deg) scale(1.02); }
      }

      .mm-footer { display:grid; grid-template-columns: 1fr auto auto; gap:12px; align-items:center;
        max-width:920px; margin:16px auto 0; width:100%;
      }
      .mm-combo { font-weight:800; opacity:0.9; }
      .mm-bar { height:8px; background:rgba(255,255,255,0.08); border-radius:999px; overflow:hidden; margin-top:6px; }
      .mm-bar-fill { height:100%; background:linear-gradient(90deg, var(--accent), #fff); box-shadow:0 0 16px var(--glow); }

      .mm-streak { font-weight:800; opacity:0.9; }

      .mm-ach { background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.1); border-radius:12px; padding:10px;
        max-width:360px; }
      .mm-ach-title { font-size:12px; opacity:0.8; margin-bottom:6px; font-weight:800; letter-spacing:0.5px; }
      .mm-ach-empty { opacity:0.6; font-size:12px; }
      .mm-ach-grid { display:flex; gap:6px; flex-wrap:wrap; }
      .mm-ach-badge { background:rgba(255,255,255,0.08); padding:6px 8px; border-radius:10px; font-size:12px; border:1px solid rgba(255,255,255,0.12); }

      .mm-modal {
        position:fixed; inset:0; display:flex; align-items:center; justify-content:center; backdrop-filter: blur(6px);
        background: rgba(0,0,0,0.35); z-index:100;
      }
      .mm-modal-card {
        width:min(92vw, 480px); border-radius:16px; padding:18px; background: linear-gradient(180deg, rgba(255,255,255,0.06), rgba(0,0,0,0.25)), var(--card);
        border:1px solid rgba(255,255,255,0.14); box-shadow: 0 0 30px rgba(0,0,0,0.35), 0 0 16px var(--glow) inset;
        text-align:center;
        animation: mm-rise .28s ease;
      }
      @keyframes mm-rise { from { transform: translateY(10px); opacity:0 } to { transform: translateY(0); opacity:1 } }
      .mm-modal-title { font-weight:900; font-size:22px; margin-bottom:6px; }
      .mm-modal-stars { font-size:26px; margin-bottom:8px; }
      .mm-modal-stats { display:grid; gap:6px; justify-items:center; margin:8px 0 12px; }

      @media (max-width:640px) {
        .mm-topstats { justify-content:flex-end; }
        .mm-footer { grid-template-columns: 1fr; }
        .mm-ach { order:3; }
      }
    `}</style>
  );
}
